import * as React from "react";
import { useCompany } from "@/context/CompanyContext";

/**
 * useTerminology — resolves canonical nouns to the company's chosen
 * display label. Falls back to the canonical noun (Title-cased) if
 * unset.
 *
 * Extended in Phase 15.5b (vocabulary layer v2) to cover the full
 * canonical set from `04-platform/05-vocabulary-not-aesthetics.md`:
 *
 *   commitment       — booking/class/job/appointment/consignment/lesson
 *   recipient        — customer/member/patient/consignee/guest
 *   position         — staff role label (Bartender/Technician/Driver)
 *   balance          — membership/class pack/treatment course
 *   document         — certification/MOT/registration/licence
 *   site             — studio/workshop/practice/depot
 *   margin_line      — cost per class / profit per job / cost per drop
 *
 * Legacy aliases kept for backwards compatibility with existing pack
 * data (booking/customer/service/member).
 *
 * Usage:
 *   const t = useTerminology();
 *   <h1>{t("commitments")}</h1>            // -> "Viewings" for automotive
 *   <span>{t("recipient", "lower")}</span> // -> "patient" for clinical
 */
const CANONICAL_TITLE = {
  // Legacy — kept for compat
  booking: "Booking",     bookings: "Bookings",
  member: "Member",       members: "Members",
  service: "Service",     services: "Services",
  customer: "Customer",
  // Full canonical set — Phase 15.5b
  commitment: "Booking",  commitments: "Bookings",
  recipient: "Customer",  recipients: "Customers",
  position: "Position",   positions: "Positions",
  balance: "Balance",     balances: "Balances",
  document: "Document",   documents: "Documents",
  site: "Site",           sites: "Sites",
  margin_line: "Margin",
};

/**
 * Legacy → canonical alias table. If a company map only has the old
 * key, resolve to the new key transparently. Same for reverse — if the
 * map only has the new key and legacy is asked for, use it.
 */
const ALIAS = {
  booking: "commitment",   bookings: "commitments",
  customer: "recipient",   customers: "recipients",
  member: "position",      members: "positions",
  // NOTE: `member` maps to `position` because a "team member" in a
  // roster context IS the position holder. We keep both keys populated
  // in vertical seeds so no fallback drift happens.
};

function resolve(map, key) {
  if (map[key]) return map[key];
  const alt = ALIAS[key];
  if (alt && map[alt]) return map[alt];
  const rev = Object.entries(ALIAS).find(([, v]) => v === key);
  if (rev && map[rev[0]]) return map[rev[0]];
  return CANONICAL_TITLE[key] || key;
}

export function useTerminology() {
  const { company } = useCompany();
  const map = React.useMemo(() => company?.terminology || {}, [company]);

  return React.useCallback(
    /**
     * @param {string} key      canonical noun (see CANONICAL_TITLE keys)
     * @param {"upper"|"lower"|null} casing
     */
    (key, casing = null) => {
      const raw = resolve(map, key);
      if (casing === "lower") return raw.toLowerCase();
      if (casing === "upper") return raw.toUpperCase();
      return raw;
    },
    [map],
  );
}

/**
 * Non-hook variant for cases outside React (e.g. tooltips built from
 * `company` fetched imperatively).
 */
export function termFor(company, key, casing = null) {
  const map = company?.terminology || {};
  const raw = resolve(map, key);
  if (casing === "lower") return raw.toLowerCase();
  if (casing === "upper") return raw.toUpperCase();
  return raw;
}
